import Vue from 'vue';
import Router, { RouteConfig } from 'vue-router';

import { notFoundRoutes } from '@/views/notfound/routes';
import { loginRoutes } from '@/views/login/routes';
import { logoutRoutes } from '@/views/logout/routes';
import { homeRoute, HOME_PATH } from '@/views/home/routes';
import { rulesEngineRoutes } from '@/views/rules-engine/routes';
import { prescriptionRoutes } from '@/views/prescription/routes';
import { usersRoutes } from '@/views/users/routes';
import { businessunitsRoutes } from '@/views/business-unit/routes';
import { clientsRoutes } from '@/views/clients/routes';
import { doctorsRoutes } from '@/views/doctors/routes';
import { expressionsRoutes } from '@/views/expressions/routes';
import { groupsRoutes } from '@/views/groups/routes';
import { materialsRoutes } from '@/views/materials/routes';
import { sievesRoutes } from '@/views/sieves/routes';
import { patientsRoutes } from '@/views/patients/routes';
import { productsRoutes } from '@/views/products/routes';
import { parametersRoutes } from '@/views/parameters/routes';
import { clientUnitRoutes } from '@/views/client-units/routes';
import { prescriptionTemplatesRoutes } from '@/views/prescription-templates/routes';
import { therapyRoutes } from '@/views/therapy/routes';
import { simpleTherapyRoutes } from '@/views/simple-therapy/routes';
import { enteralRoutes } from '@/views/enteral/routes';
import { reportRoutes } from '@/views/reports/routes';
import { dashboardRoutes } from '@/views/dashboard/routes';
import { holidayCalendarRoutes } from '@/views/holiday-calendar/routes';
import {
  getCurrentUserPermission,
  updateUserPermissionsFromLocal,
  getToken,
  setRedirectAfterLogin,
  getRedirectAfterLogin,
  getLocalBusinessUnit,
  removeRedirectAfterLogin,
  setRedirectEmpresaAfterLogin,
  setRedirectLinkOrigin,
} from '@/services/session-data';
import * as changeClientDialog$ from '@/subjects/change-client-dialog';
import * as $Logout from '@/subjects/logout';
import { teamRoutes } from './views/teams/routes';
import { bagModelsRoutes } from './views/bag-models/routes';
import { clinicStatesRoutes } from './views/clinic-states/routes';
import { clinicSituationsRoutes } from './views/clinic-situations/routes';
import { notificationsRoutes } from './views/notifications/routes';
import * as changeBusinessUnit$ from '@/subjects/change-businessunit-dialog';
import { pendingPrescriptionsRoutes } from './views/pending-prescriptions/routes';
import { shippingRoutes } from './views/shipping/routes';
import { splittedBagConfigRoutes } from './views/splitted-bag-config/routes';
import { deliveryAddressRoutes } from './views/address/routes';
import { crmRoutes } from './views/crm/routes';

Vue.use(Router);

/*
  ROUTING RULES

  A Route must have the following fields:
  - path: The path it will appear on the browser
  - name: Parent Name.Route Name
  - component: The componet it will render
  - meta: { requiresAuth: true|false, hideFromMenu: true|false }
*/

export const routes: RouteConfig[] = [
  ...notFoundRoutes,
  ...loginRoutes,
  ...logoutRoutes,
  {
    ...homeRoute,
    children: [
      /* menu */
      ...prescriptionRoutes,
      ...businessunitsRoutes, // Empresas
      ...clientsRoutes,
      ...clientUnitRoutes,
      ...doctorsRoutes,
      ...teamRoutes,
      ...patientsRoutes,
      ...deliveryAddressRoutes,
      ...materialsRoutes,
      ...sievesRoutes, // Crivos
      ...productsRoutes,
      ...groupsRoutes,
      ...expressionsRoutes,
      ...rulesEngineRoutes,
      ...prescriptionTemplatesRoutes,
      ...parametersRoutes,
      ...usersRoutes,
      ...bagModelsRoutes,
      ...splittedBagConfigRoutes,
      ...therapyRoutes,
      ...clinicStatesRoutes,
      ...clinicSituationsRoutes,
      ...simpleTherapyRoutes,
      ...enteralRoutes,
      ...reportRoutes,
      ...dashboardRoutes,
      ...notificationsRoutes,
      ...pendingPrescriptionsRoutes,
      ...shippingRoutes,
      ...crmRoutes,
      ...holidayCalendarRoutes
    ],
  },
];

const router = new Router({
  mode: 'history',
  routes,
});

router.beforeEach((to, from, next) => {
  if (to.query.redirect) {
    setRedirectAfterLogin(decodeURIComponent(to.query.redirect.toString()));
    if (to.query.emp) {
      if (getLocalBusinessUnit() == null || getLocalBusinessUnit().id != to.query.emp) {
        const authUser = getToken();
        if (!!authUser) {
          changeBusinessUnit$.select(to.query.emp);
          return;
        } else {
          setRedirectEmpresaAfterLogin(to.query.emp.toString());
        }
      }
    }
    if (to.query.or) {
      setRedirectLinkOrigin(to.query.or.toString());
    }
  }
  if (to.meta.requiresAuth) {
    const authUser = getToken();
    if (!!authUser) {
      changeClientDialog$.clientIsSelected();
      getCurrentUserPermission().subscribe((permissions) => {
        updateUserPermissionsFromLocal(permissions).subscribe();
      });
      if (getRedirectAfterLogin() && getLocalBusinessUnit()) {
        let newPath = getRedirectAfterLogin();
        removeRedirectAfterLogin();
        next(newPath);
        return;
      }
      next();
    } else {      
      if (to.path === HOME_PATH) {
        $Logout.logout();
      } else {
        $Logout.sessionExpired(null);
      }
    }
    return;
  }
  next();
});

export default router;
